import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Every floating panel in the app goes through here, and the reason is
 * clipping, not tidiness.
 *
 * A dropdown rendered inside its trigger is at the mercy of whatever the
 * trigger's ancestors do: the toolbar has `overflow-x: auto` so the Examples
 * menu was cut off at the toolbar's bottom edge and simply invisible; the grid
 * body scrolls, so the formula suggestion list and the chart settings panel
 * were both clipped by it near the bottom of the sheet. No amount of z-index
 * fixes that — overflow clipping happens first.
 *
 * So a popover renders through a PORTAL to <body>, positioned in viewport
 * coordinates, outside every scroll container in the app. It then flips and
 * clamps to stay on screen, and closes on an outside press, Escape, or when
 * whatever it was anchored to scrolls away.
 */

export interface AnchorRect {
  x: number;
  y: number;
  /** Width and height of the thing being anchored to. Zero for a point. */
  w?: number;
  h?: number;
}

export interface PopoverProps {
  anchor: AnchorRect;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  /** Align the popover's left edge with the anchor's left ('start') or right ('end'). */
  align?: 'start' | 'end';
  /** Gap between the anchor and the popover, in px. */
  gap?: number;
  /** Close when the user presses outside. Default true. */
  closeOnOutsidePress?: boolean;
  /** Keep the press that closes it from reaching what is underneath. */
  swallowOutsidePress?: boolean;
  /** Re-close when an ancestor scrolls — a menu pinned to a moved cell is wrong. */
  closeOnScroll?: boolean;
}

const MARGIN = 8;

export function Popover({
  anchor,
  onClose,
  children,
  className,
  align = 'start',
  gap = 4,
  closeOnOutsidePress = true,
  swallowOutsidePress = false,
  closeOnScroll = false,
}: PopoverProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Measure, then place. Rendered invisibly for one frame rather than at a
  // guessed position, because a menu that visibly jumps is worse than one that
  // appears a frame later.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const anchorW = anchor.w ?? 0;
    const anchorH = anchor.h ?? 0;

    let left = align === 'end' ? anchor.x + anchorW - box.width : anchor.x;
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - box.width - MARGIN));

    // Below by default; above when there is no room below but there is above.
    const below = anchor.y + anchorH + gap;
    const fitsBelow = below + box.height <= window.innerHeight - MARGIN;
    const above = anchor.y - gap - box.height;
    const fitsAbove = above >= MARGIN;
    let top = fitsBelow || !fitsAbove ? below : above;
    top = Math.max(MARGIN, Math.min(top, window.innerHeight - box.height - MARGIN));

    setPos({ left, top });
  }, [anchor.x, anchor.y, anchor.w, anchor.h, align, gap, children]);

  // On the stack once, for as long as it is open. Not in the listener effect
  // below: that one re-runs on every render, and re-pushing would move a
  // parent above the popover opened from it.
  useEffect(() => {
    const self = ref.current;
    if (!self) return;
    OPEN.push(self);
    return () => {
      const at = OPEN.indexOf(self);
      if (at >= 0) OPEN.splice(at, 1);
    };
  }, []);

  useEffect(() => {
    /*
     * Arm on the next frame.
     *
     * Right-clicking a cell outside the selection selects it first, and
     * selecting a partly-offscreen cell scrolls it into view — so the close-on-
     * scroll listener fired on the popover's own opening scroll and it vanished
     * the instant it appeared. Only scrolls after the first frame count.
     */
    let armed = false;
    const raf = requestAnimationFrame(() => {
      armed = true;
    });
    const self = ref.current;
    /*
     * A popover opened FROM this one — a searchable field list inside the
     * right-click menu — is a separate portal, so a press in it is "outside"
     * this one. It is not: the nested list is part of what this menu is doing.
     * Presses, scrolls and Escape inside anything opened after this one are
     * left to that one.
     */
    const inNested = (target: EventTarget | null): boolean => {
      if (!(target instanceof Node) || !self) return false;
      const at = OPEN.indexOf(self);
      return OPEN.slice(at + 1).some((el) => el.contains(target));
    };
    const isTop = (): boolean => OPEN[OPEN.length - 1] === self;

    const onDown = (e: PointerEvent): void => {
      if (ref.current?.contains(e.target as Node)) return;
      if (inNested(e.target)) return;
      if (!closeOnOutsidePress) return;
      if (swallowOutsidePress) {
        e.preventDefault();
        e.stopPropagation();
      }
      onClose();
    };
    const onKey = (e: KeyboardEvent): void => {
      // Escape closes the innermost one only, like peeling a stack.
      if (e.key === 'Escape' && isTop()) onClose();
    };
    const onScrollAway = (e?: Event): void => {
      if (!armed) return;
      /*
       * A scroll INSIDE the popover is not the popover scrolling away.
       *
       * The listener is on window in the capture phase, so it sees every
       * element's scroll — including the filter drawer's own value list, which
       * meant the list closed the menu the moment anyone tried to scroll it.
       */
      const target = e?.target;
      if (target instanceof Node && ref.current?.contains(target)) return;
      if (e && inNested(e.target)) return;
      onClose();
    };

    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    if (closeOnScroll) window.addEventListener('scroll', onScrollAway, true);
    window.addEventListener('resize', onScrollAway);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      if (closeOnScroll) window.removeEventListener('scroll', onScrollAway, true);
      window.removeEventListener('resize', onScrollAway);
    };
  }, [onClose, closeOnOutsidePress, swallowOutsidePress, closeOnScroll]);

  return createPortal(
    <div
      ref={ref}
      className={`gt-popover${className ? ` ${className}` : ''}`}
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body
  );
}

/** Popovers open now, oldest first — so a nested one knows it is nested. */
const OPEN: HTMLDivElement[] = [];

/** The viewport rect of an element, as a Popover anchor. */
export function rectOf(el: Element | null): AnchorRect {
  if (!el) return { x: 0, y: 0 };
  const box = el.getBoundingClientRect();
  return { x: box.left, y: box.top, w: box.width, h: box.height };
}
